import { useRef, useState } from "react";
import axios from "axios";
import { Plus, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UploadBookDialog = ({ onUploaded }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [blurb, setBlurb] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const reset = () => {
    setName("");
    setBlurb("");
    setFiles([]);
    if (fileInput.current) fileInput.current.value = "";
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Give the tome a name first");
      return;
    }
    if (files.length === 0) {
      toast.error("Add page images or one zip archive");
      return;
    }
    const form = new FormData();
    form.append("name", name.trim());
    form.append("blurb", blurb.trim());
    files.forEach((f) => form.append("files", f));
    setBusy(true);
    try {
      await axios.post(`${API}/books/upload`, form);
      toast.success(`"${name.trim()}" is now on the shelf`);
      setOpen(false);
      reset();
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="add-tome-btn"
          variant="outline"
          className="rounded-full transition-transform duration-200 hover:-translate-y-0.5"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add a tome
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="upload-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Archive a new tome</DialogTitle>
        </DialogHeader>
        <div className="mt-2 space-y-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Tome name</p>
            <Input
              data-testid="upload-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Aurora Hymnal"
              className="mt-2"
            />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Blurb (optional)</p>
            <Textarea
              data-testid="upload-blurb-input"
              value={blurb}
              onChange={(e) => setBlurb(e.target.value)}
              placeholder="One line for the shelf card"
              rows={2}
              className="mt-2"
            />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pages</p>
            <label
              data-testid="upload-dropzone"
              className="mt-2 flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border bg-card/50 px-4 py-8 text-center transition-colors duration-200 hover:border-primary/60"
            >
              <UploadCloud className="h-6 w-6 text-primary" />
              <span className="text-sm text-muted-foreground">
                {files.length > 0
                  ? `${files.length} file${files.length > 1 ? "s" : ""} selected`
                  : "Choose page images or one .zip"}
              </span>
              <input
                ref={fileInput}
                data-testid="upload-files-input"
                type="file"
                multiple
                accept="image/*,.zip"
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </label>
            <p className="mt-2 text-xs text-muted-foreground">
              Pages are ordered by filename and stored in the archive's media vault.
            </p>
          </div>
          <Button
            data-testid="upload-submit-btn"
            onClick={submit}
            disabled={busy}
            className="w-full rounded-full"
          >
            {busy ? "Archiving…" : "Place on the shelf"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
